import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function DeleteAgencyDialog({ agency, onClose, onDeleted }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = name.trim().toLowerCase() === (agency.name || "").trim().toLowerCase();

  const run = async () => {
    setBusy(true);
    try {
      const res = await axios.delete(`${API}/platform/agencies/${agency.id}`, { data: { confirm_name: name } });
      const n = Object.values(res.data.removed || {}).reduce((a, b) => a + b, 0);
      toast.success(`Deleted "${agency.name}" and ${n} related records`);
      onDeleted();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="platform-delete-dialog">
        <DialogHeader>
          <DialogTitle className="text-rose-700">Delete agency permanently</DialogTitle>
          <DialogDescription>
            This removes <strong>{agency.name}</strong> with all its users, clients, employees, documents and records. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-xs">Type the agency name to confirm</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={agency.name} data-testid="platform-delete-confirm-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" disabled={!ok || busy} onClick={run} className="gap-1" data-testid="platform-delete-confirm-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Delete agency
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BulkDeleteDialog({ agencies, onClose, onDeleted }) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/platform/agencies/bulk-delete`, { ids: agencies.map((a) => a.id), confirm });
      toast.success(`Deleted ${res.data.deleted} agencies`);
      onDeleted();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="platform-bulk-delete-dialog">
        <DialogHeader>
          <DialogTitle className="text-rose-700">Delete {agencies.length} agencies</DialogTitle>
          <DialogDescription>All data for these agencies will be permanently removed.</DialogDescription>
        </DialogHeader>
        <ul className="max-h-48 overflow-y-auto rounded-md border border-slate-200 divide-y divide-slate-100 text-sm">
          {agencies.map((a) => <li key={a.id} className="px-3 py-1.5 text-slate-700">{a.name}{a.users ? <span className="text-xs text-slate-400"> · {a.users} users</span> : null}</li>)}
        </ul>
        <div>
          <Label className="text-xs">Type DELETE to confirm</Label>
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE" data-testid="platform-bulk-confirm-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" disabled={confirm !== "DELETE" || busy} onClick={run} className="gap-1" data-testid="platform-bulk-confirm-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Delete {agencies.length} agencies
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
